// Conversas contra o WootDash — GET /conversas, GET /conversas/{id}, mensagens (com
// paginação), atribuir (agente/time), status/resolver, etiquetas e envio de template dentro
// da conversa (Dashboard real + port do ConvDrawer, ver PLANO_MIGRACAO_WOOTDASH.md).
import { WOOTDASH_BASE, wootdashAuthHeaders } from '@/api/wootdash'

export type ConversationStatus = 'Open' | 'Pending' | 'Resolved' | 'Closed'

export interface ConversationListItem {
  id: string
  contactId: string
  contactName: string
  contactPhone: string | null
  contactAvatarUrl: string | null
  status: number
  agentId: string | null
  agentName: string | null
  flowId: string | null
  flowName: string | null
  teamId: string | null
  teamName: string | null
  tagNames: string[]
  lastMessagePreview: string | null
  lastMessageSenderType: string | null
  lastMessageAt: string | null
  resolvedAt: string | null
  createdAt: string
  /** Mensagens do contato que o agente logado ainda não viu (GET /conversas). Nos itens que chegam por
   * SignalR (ListaDeConversasAlterada) vem 0 — o rail soma sozinho (useRailConversations.ts). */
  unreadCount?: number
  /** Protocolo de atendimento gravado pelo bot (bloco Protocolo). */
  protocol?: string | null
  /** Adiada até quando (Pendente com prazo). Nulo = não está adiada. */
  snoozedUntil?: string | null
}

export interface ConversationDetail {
  id: string
  contactId: string
  contactName: string
  contactPhone: string | null
  contactEmail: string | null
  contactAvatarUrl: string | null
  status: number
  agentId: string | null
  agentName: string | null
  flowId: string | null
  flowName: string | null
  teamId: string | null
  teamName: string | null
  slaDueAt: string | null
  slaBreachedAt: string | null
  firstResponseAt: string | null
  slaPhase: string | null
  tagNames: string[]
  createdAt: string
  protocol?: string | null
  /** Adiada até quando (Pendente com prazo). Nulo = não está adiada. */
  snoozedUntil?: string | null
  /** Sessão de bot mais recente (versão do fluxo em que está ou passou). Nulo = nunca passou pelo bot. */
  bot?: ConversationBotInfo | null
}

/** Versão do fluxo de bot da conversa — igual a ConversationBotInfo do servidor. */
export interface ConversationBotInfo {
  botId: string
  botName: string
  versionNumber: number
  versionPublishedAt: string
  status: 'running' | 'handoff' | 'ended' | 'expired'
  kind: 'flow' | 'rating'
  waitingOn: string | null
  latestVersionNumber: number | null
  startedAt: string
}

/** Andamento de uma mensagem nossa no WhatsApp (tiques). Nulo = não saiu pelo WhatsApp. */
export type DeliveryStatus = 'sent' | 'delivered' | 'read' | 'failed'

export interface ConversationMessage {
  id: string
  senderType: 'Contact' | 'Agent' | 'Bot' | 'System'
  senderId: string | null
  body: string | null
  mediaUrl: string | null
  mediaMimeType: string | null
  isPrivateNote: boolean
  createdAt: string
  deliveryStatus?: DeliveryStatus | null
  deliveryError?: string | null
  /** "copilot" = enviada sozinha pelo copiloto de IA (em nome do atendente). */
  origin?: string | null
}

export interface TemplateSendOutcome {
  contactId: string
  contactName: string
  success: boolean
  whatsappMessageId: string | null
  errorMessage: string | null
}

// STATUS_LABEL/STATUS_ORDER refletem o enum ConversationStatus do backend (Open=0,
// Pending=1, Resolved=2, Closed=3) — o campo `status` na resposta vem como número.
export const STATUS_LABEL: Record<number, string> = { 0: 'Aberta', 1: 'Pendente', 2: 'Resolvida', 3: 'Fechada' }

function authHeaders(token: string, json = false) {
  return { ...wootdashAuthHeaders(token), ...(json ? { 'Content-Type': 'application/json' } : {}) }
}

export async function listConversations(
  token: string,
  opts: {
    onlyMine?: boolean
    statuses?: ConversationStatus[]
    unitId?: string
    uf?: string
    teamId?: string
    tagId?: string
    flowId?: string
    /** "Esconder Fluxo" — some do resultado e do total; esconder o Geral (nº 1) esconde também
     * conversa sem fluxo (regra do backend). */
    excludeFlowIds?: string[]
    contactId?: string
    search?: string
    /** yyyy-MM-dd (input type="date") — convertido pro início/fim do dia em ISO antes de mandar. */
    createdFrom?: string
    createdTo?: string
    /** Filtro Agendor (só vale pra quem tem acesso ao Agendor): contato vinculado ou não importado. */
    agendor?: 'vinculado' | 'nao-vinculado'
    page?: number
    pageSize?: number
  },
): Promise<{ conversations: ConversationListItem[]; total: number }> {
  const params = new URLSearchParams({ pagina: String(opts.page ?? 1), tamanho: String(opts.pageSize ?? 20) })
  if (opts.onlyMine) params.set('minhas', 'true')
  if (opts.statuses?.length) params.set('status', opts.statuses.join(','))
  if (opts.unitId) params.set('unidadeId', opts.unitId)
  if (opts.uf) params.set('uf', opts.uf)
  if (opts.teamId) params.set('timeId', opts.teamId)
  if (opts.tagId) params.set('etiquetaId', opts.tagId)
  if (opts.flowId) params.set('fluxoId', opts.flowId)
  if (opts.excludeFlowIds?.length) params.set('excluirFluxos', opts.excludeFlowIds.join(','))
  if (opts.agendor) params.set('agendor', opts.agendor)
  if (opts.contactId) params.set('contatoId', opts.contactId)
  if (opts.search) params.set('busca', opts.search)
  if (opts.createdFrom) params.set('criadoDesde', new Date(`${opts.createdFrom}T00:00:00`).toISOString())
  if (opts.createdTo) params.set('criadoAte', new Date(`${opts.createdTo}T23:59:59`).toISOString())
  try {
    const res = await fetch(`${WOOTDASH_BASE}/conversas?${params}`, { headers: authHeaders(token) })
    if (!res.ok) return { conversations: [], total: 0 }
    const data = await res.json()
    return { conversations: data.conversations ?? [], total: data.total ?? 0 }
  } catch {
    return { conversations: [], total: 0 }
  }
}

export async function getConversation(token: string, id: string): Promise<ConversationDetail | null> {
  try {
    const res = await fetch(`${WOOTDASH_BASE}/conversas/${id}`, { headers: authHeaders(token) })
    if (!res.ok) return null
    return await res.json()
  } catch {
    return null
  }
}

export async function getMessages(
  token: string, id: string, opts?: { before?: string; limit?: number },
): Promise<ConversationMessage[]> {
  const params = new URLSearchParams()
  if (opts?.before) params.set('antes', opts.before)
  if (opts?.limit) params.set('limite', String(opts.limit))
  const qs = params.toString()
  try {
    const res = await fetch(`${WOOTDASH_BASE}/conversas/${id}/mensagens${qs ? `?${qs}` : ''}`, { headers: authHeaders(token) })
    if (!res.ok) return []
    return await res.json()
  } catch {
    return []
  }
}

export async function sendMessage(
  token: string, id: string, body: string | null, isPrivateNote: boolean,
  media?: { mediaUrl: string; mediaMimeType: string; mediaFileName?: string },
): Promise<boolean> {
  const res = await fetch(`${WOOTDASH_BASE}/conversas/${id}/mensagens`, {
    method: 'POST', headers: authHeaders(token, true),
    body: JSON.stringify({ body, isPrivateNote, mediaUrl: media?.mediaUrl ?? null, mediaMimeType: media?.mediaMimeType ?? null, mediaFileName: media?.mediaFileName ?? null }),
  })
  return res.ok
}

/** "Visto" no WhatsApp do contato (última mensagem dele). Falha é silenciosa: é só um aviso. */
export async function markConversationSeen(token: string, id: string): Promise<void> {
  try { await fetch(`${WOOTDASH_BASE}/conversas/${id}/visto`, { method: 'POST', headers: authHeaders(token) }) } catch { /* sem rede: ignora */ }
}

/** Zera o contador de não lidas do agente logado nesta conversa (badge) — não manda nada ao contato. */
export async function markConversationRead(token: string, id: string): Promise<void> {
  try { await fetch(`${WOOTDASH_BASE}/conversas/${id}/lida`, { method: 'POST', headers: authHeaders(token) }) } catch { /* sem rede: ignora */ }
}

/** "Digitando…" no WhatsApp do contato (dura até 25 s ou até a resposta). Também marca como vista. */
export async function sendTypingIndicator(token: string, id: string): Promise<void> {
  try { await fetch(`${WOOTDASH_BASE}/conversas/${id}/digitando`, { method: 'POST', headers: authHeaders(token) }) } catch { /* sem rede: ignora */ }
}

export async function assignConversation(token: string, id: string, agentId: string): Promise<boolean> {
  const res = await fetch(`${WOOTDASH_BASE}/conversas/${id}/atribuir`, {
    method: 'POST', headers: authHeaders(token, true), body: JSON.stringify({ agentId }),
  })
  return res.ok
}

export async function assignConversationTeam(token: string, id: string, teamId: string): Promise<boolean> {
  const res = await fetch(`${WOOTDASH_BASE}/conversas/${id}/atribuir-time`, {
    method: 'POST', headers: authHeaders(token, true), body: JSON.stringify({ teamId }),
  })
  return res.ok
}

export async function resolveConversation(token: string, id: string): Promise<boolean> {
  const res = await fetch(`${WOOTDASH_BASE}/conversas/${id}/resolver`, { method: 'POST', headers: authHeaders(token) })
  return res.ok
}

// Só Open/Pending — resolver continua um endpoint à parte (resolveConversation acima).
// "Adiar" no ConvDrawer antigo virou só marcar Pendente aqui: o WootDash não distingue
// adiada/silenciada/pendente por falta de resposta (ver ExecuteMacroCommandHandler), então
// não há hora de retorno pra pedir nem pra mostrar.
export async function changeConversationStatus(token: string, id: string, status: 'Open' | 'Pending'): Promise<boolean> {
  const res = await fetch(`${WOOTDASH_BASE}/conversas/${id}/status`, {
    method: 'POST', headers: authHeaders(token, true), body: JSON.stringify({ status }),
  })
  return res.ok
}

/** Adiar: fica Pendente até a hora (de 1 min a 30 dias); vencido, volta pra Aberta e o agente recebe o aviso.
 *  Devolve o prazo gravado (ISO) ou lança com o motivo. */
export async function snoozeConversation(token: string, id: string, until: Date): Promise<string> {
  const res = await fetch(`${WOOTDASH_BASE}/conversas/${id}/adiar`, {
    method: 'POST', headers: authHeaders(token, true), body: JSON.stringify({ until: until.toISOString() }),
  })
  const data = await res.json().catch(() => null)
  if (!res.ok) {
    throw new Error(data?.reason === 'InvalidTime' ? 'Escolha um horário entre 1 minuto e 30 dias à frente.'
      : data?.reason === 'ConversationClosed' ? 'Conversa resolvida não pode ser adiada.' : 'Não deu pra adiar a conversa.')
  }
  return data.snoozedUntil
}

export async function addConversationTag(token: string, id: string, tagId: string): Promise<boolean> {
  const res = await fetch(`${WOOTDASH_BASE}/conversas/${id}/etiquetas/${tagId}`, { method: 'POST', headers: authHeaders(token) })
  return res.ok
}

export async function removeConversationTag(token: string, id: string, tagId: string): Promise<boolean> {
  const res = await fetch(`${WOOTDASH_BASE}/conversas/${id}/etiquetas/${tagId}`, { method: 'DELETE', headers: authHeaders(token) })
  return res.ok
}

export async function sendTemplateInConversation(
  token: string, id: string, templateId: string, manualVariableValues?: Record<number, string>, headerImageUrl?: string,
): Promise<boolean> {
  const res = await fetch(`${WOOTDASH_BASE}/conversas/${id}/mensagens/modelo`, {
    method: 'POST', headers: authHeaders(token, true),
    body: JSON.stringify({ templateId, manualVariableValues: manualVariableValues ?? null, headerImageUrl: headerImageUrl ?? null }),
  })
  return res.ok
}
